/** Probate 171 — Stream F */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  probateFieldLabelClass,
  probateInputClass,
  ProbateBackLink,
} from './probateStepUi';
import { useProbateStep } from './useProbateStep';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { SETTLEMENT_PLANNER_LAYOUT } from '../../settlement/settlementTheme';
import {
  extractFinancialAccountsFromSettlementData,
  extractPersonalPropertyFrom160,
  extractRealEstate155,
  extractVehicles155,
} from '../distribution/streamJSources';

const STEP_ID = 'prob-1';
const FORM_FIELDS = 'domicileState,estateValueEstimate,willStatus';
const STATE_OPTIONS = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA',
  'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY',
  'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX',
  'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
] as const;

type AssistSummary = {
  assetInventoryCount: number;
  financialAccountCount: number;
  propertyCount: number;
  vehicleCount: number;
};

export default function SFrame171() {
  const {
    estateId,
    isArchived,
    error,
    formValues,
    setField,
  } = useProbateStep(STEP_ID, FORM_FIELDS);

  const ro = !!isArchived;
  const { loadFrameData } = useSettlementSession(estateId, { readOnly: true });
  const [assistSummary, setAssistSummary] = useState<AssistSummary>({
    assetInventoryCount: 0,
    financialAccountCount: 0,
    propertyCount: 0,
    vehicleCount: 0,
  });

  useEffect(() => {
    if (!estateId) {
      setAssistSummary({
        assetInventoryCount: 0,
        financialAccountCount: 0,
        propertyCount: 0,
        vehicleCount: 0,
      });
      return;
    }

    let cancelled = false;
    (async () => {
      const [inventory160, accounts146, inventory155] = await Promise.all([
        loadFrameData('sframe-160'),
        loadFrameData('sframe-146'),
        loadFrameData('sframe-155'),
      ]);

      const data160 = (inventory160?.data as Record<string, unknown>) ?? null;
      const data146 = (accounts146?.data as Record<string, unknown>) ?? null;
      const data155 = (inventory155?.data as Record<string, unknown>) ?? null;

      const assetInventoryCount = extractPersonalPropertyFrom160(data160).length;
      const financialAccountCount = extractFinancialAccountsFromSettlementData(data146).length;
      const propertyCount = extractRealEstate155(data155).length;
      const vehicleCount = extractVehicles155(data155).length;

      if (!cancelled) {
        setAssistSummary({
          assetInventoryCount,
          financialAccountCount,
          propertyCount,
          vehicleCount,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData]);

  const withEstateId = useMemo(
    () => (path: string) => (estateId ? `${path}/${estateId}` : path),
    [estateId]
  );

  return (
    <div className={SETTLEMENT_PLANNER_LAYOUT.container}>
      <div className={SETTLEMENT_PLANNER_LAYOUT.contentColumn}>
      <ProbateBackLink estateId={estateId} />
      {error ? (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      ) : null}

      <h1 className={SETTLEMENT_PLANNER_LAYOUT.title}>Determine If Probate Is Needed</h1>
      <p className="text-stone-700 text-sm mt-3 mb-3">
        Review the estate, ownership of assets, and existing documents to understand whether probate may be required.
      </p>
      <p className="text-stone-600 text-sm mb-6">
        Not all estates require probate. The process depends on asset ownership, beneficiary designations, estate value,
        and whether assets are held in a trust.
      </p>

      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-4 space-y-4">
        <h2 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">What this is</h2>
        <p className="text-sm text-stone-700">
          Assess whether the estate must go through probate based on assets, ownership, and state requirements.
        </p>

        <h2 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">Why it matters</h2>
        <p className="text-sm text-stone-700">
          Determines the legal process required and whether court involvement is necessary.
        </p>

        <h2 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">Key actions</h2>
        <ul className="list-disc pl-5 space-y-1 text-sm text-stone-700">
          <li>Identify assets that require probate (no joint owner or beneficiary)</li>
          <li>Estimate total estate value</li>
          <li>Confirm whether a will exists</li>
          <li>Determine if simplified procedures apply (if applicable)</li>
          <li>Decide whether legal support is needed</li>
        </ul>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-4 space-y-4">
        <h2 className="text-lg font-semibold text-stone-900">Understand What Goes Through Probate</h2>
        <div>
          <h3 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">What this is</h3>
          <p className="text-sm text-stone-700 mt-1">
            Clarifies which assets go through probate and which transfer outside of it.
          </p>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">Why it matters</h3>
          <p className="text-sm text-stone-700 mt-1">
            Helps you accurately determine whether probate is required and what may be included in the estate process.
          </p>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">Key actions</h3>
          <ul className="list-disc pl-5 mt-1 space-y-1 text-sm text-stone-700">
            <li>Review asset ownership (individual vs joint)</li>
            <li>Identify accounts with named beneficiaries</li>
            <li>Separate probate vs non-probate assets</li>
            <li>Identify assets held in a trust</li>
          </ul>
        </div>

        <details className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-3">
          <summary className="cursor-pointer font-semibold text-stone-800">What assets go through probate?</summary>
          <div className="pt-3 text-sm text-stone-700 space-y-3">
            <div>
              <p className="font-medium">Probate assets typically include:</p>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>Assets owned solely in the deceased&apos;s name</li>
                <li>Real estate without joint ownership</li>
                <li>Accounts without a named beneficiary</li>
              </ul>
            </div>
            <div>
              <p className="font-medium">Non-probate assets typically include:</p>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>Joint accounts with right of survivorship</li>
                <li>Accounts with named beneficiaries</li>
                <li>Assets held in a trust</li>
              </ul>
            </div>
          </div>
        </details>

        <details className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-3">
          <summary className="cursor-pointer font-semibold text-stone-800">Trust vs will — what&apos;s the difference?</summary>
          <ul className="list-disc pl-5 pt-3 space-y-1 text-sm text-stone-700">
            <li>A will directs how assets are distributed through probate</li>
            <li>A trust can allow assets to transfer outside of probate</li>
            <li>If most assets are held in a trust, probate may be limited or unnecessary</li>
          </ul>
        </details>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-4 space-y-4">
        <h2 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">Inputs</h2>
        <div>
          <label className={probateFieldLabelClass} htmlFor="domicileState">State</label>
          <select
            id="domicileState"
            className={probateInputClass}
            disabled={ro}
            value={formValues.domicileState ?? ''}
            onChange={(e) => setField('domicileState', e.target.value)}
          >
            <option value="">Select state</option>
            {STATE_OPTIONS.map((abbr) => (
              <option key={abbr} value={abbr}>{abbr}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={probateFieldLabelClass} htmlFor="estateValueEstimate">Estate value estimate</label>
          <input
            id="estateValueEstimate"
            className={probateInputClass}
            placeholder="Rough gross probate estate range"
            disabled={ro}
            value={formValues.estateValueEstimate ?? ''}
            onChange={(e) => setField('estateValueEstimate', e.target.value)}
          />
        </div>
        <div>
          <label className={probateFieldLabelClass} htmlFor="willStatus">Will status</label>
          <select
            id="willStatus"
            className={probateInputClass}
            disabled={ro}
            value={formValues.willStatus ?? ''}
            onChange={(e) => setField('willStatus', e.target.value)}
          >
            <option value="">Select will status</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">Available Data & Actions</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-900 mb-2">System-provided information</p>
            <ul className="space-y-1 text-sm text-stone-700">
              <li>Asset inventory items: <strong>{assistSummary.assetInventoryCount}</strong></li>
              <li>Financial accounts: <strong>{assistSummary.financialAccountCount}</strong></li>
              <li>Properties: <strong>{assistSummary.propertyCount}</strong></li>
              <li>Vehicles: <strong>{assistSummary.vehicleCount}</strong></li>
            </ul>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-900 mb-2">Actions still required</p>
            <ul className="space-y-2 text-sm text-stone-700">
              <li>Confirm ownership status and beneficiary designations.</li>
              <li>Review whether trust-held assets cover most of the estate.</li>
              <li>Decide if simplified probate or legal support is needed.</li>
            </ul>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-sm font-semibold text-blue-900">
          <Link className="underline" to={withEstateId('/sframe-155')}>View asset inventory</Link>
          <Link className="underline" to={withEstateId('/sframe-146')}>View financial accounts summary</Link>
          <Link className="underline" to={withEstateId('/sframe-143')}>Go to Secure &amp; Inventory Assets</Link>
        </div>
      </div>
      </div>
    </div>
  );
}
